import Phaser from "phaser";
import type { GameSettings } from "../domain/types";
import { defenseAnalytics } from "./analytics";

export type AudioCue =
  | "ui-confirm"
  | "parchment"
  | "battle-start"
  | "victory"
  | "defeat"
  | "upgrade"
  | "impact"
  | "wave-start"
  | "wave-clear"
  | "boss-arrival"
  | "tower-arrow"
  | "tower-cannon"
  | "tower-arcane"
  | "enemy-defeated";

export type MusicTheme = "title" | "battle" | "boss" | "victory" | "defeat";

export class AudioManager {
  private static sharedContext: AudioContext | null = null;
  private musicTimer: number | null = null;
  private musicStep = 0;
  private activeTheme: MusicTheme | null = null;

  constructor(private settings: GameSettings) {}

  updateSettings(settings: GameSettings): void {
    this.settings = settings;
    if (this.settings.masterVolume <= 0 || this.settings.musicVolume <= 0) this.stopMusic();
  }

  private context(): AudioContext {
    AudioManager.sharedContext ??= new AudioContext();
    return AudioManager.sharedContext;
  }

  tone(frequency: number, duration = 0.08, type: OscillatorType = "sine", volume = 0.035): void {
    if (this.settings.masterVolume <= 0 || this.settings.effectsVolume <= 0) return;
    const context = this.context();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const now = context.currentTime;
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, now);
    gain.gain.setValueAtTime(
      volume * this.settings.masterVolume * this.settings.effectsVolume,
      now,
    );
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(now);
    oscillator.stop(now + duration);
  }

  cue(cue: AudioCue): void {
    const sequences: Record<AudioCue, readonly [number, number, OscillatorType, number][]> = {
      "ui-confirm": [[620, 0.07, "triangle", 0.025]],
      parchment: [
        [220, 0.12, "sine", 0.018],
        [310, 0.1, "triangle", 0.015],
      ],
      "battle-start": [
        [220, 0.1, "square", 0.024],
        [330, 0.12, "triangle", 0.028],
        [440, 0.16, "triangle", 0.03],
      ],
      victory: [
        [392, 0.14, "triangle", 0.025],
        [523, 0.16, "triangle", 0.028],
        [659, 0.24, "triangle", 0.032],
      ],
      defeat: [
        [330, 0.18, "sine", 0.026],
        [247, 0.22, "sine", 0.028],
        [196, 0.3, "sine", 0.03],
      ],
      upgrade: [
        [520, 0.08, "triangle", 0.024],
        [760, 0.12, "triangle", 0.026],
      ],
      impact: [[120, 0.07, "square", 0.022]],
      "wave-start": [
        [330, 0.08, "triangle", 0.022],
        [440, 0.1, "triangle", 0.025],
      ],
      "wave-clear": [
        [523, 0.08, "triangle", 0.021],
        [659, 0.12, "triangle", 0.024],
      ],
      "boss-arrival": [
        [110, 0.18, "sawtooth", 0.03],
        [82, 0.24, "square", 0.032],
      ],
      "tower-arrow": [[760, 0.045, "triangle", 0.015]],
      "tower-cannon": [[92, 0.12, "square", 0.028]],
      "tower-arcane": [
        [520, 0.09, "sine", 0.02],
        [880, 0.07, "triangle", 0.013],
      ],
      "enemy-defeated": [[180, 0.055, "sawtooth", 0.012]],
    };
    sequences[cue].forEach(([frequency, duration, type, volume], index) => {
      window.setTimeout(() => this.tone(frequency, duration, type, volume), index * 85);
    });
  }

  playMusic(theme: MusicTheme): void {
    if (this.activeTheme === theme) return;
    this.stopMusic();
    this.activeTheme = theme;
    if (this.settings.masterVolume <= 0 || this.settings.musicVolume <= 0) return;
    const patterns: Record<MusicTheme, readonly number[]> = {
      title: [196, 247, 294, 247, 220, 262, 330, 262],
      battle: [110, 165, 147, 196, 123, 185, 165, 220],
      boss: [82, 82, 98, 73, 82, 110, 98, 73],
      victory: [262, 330, 392, 523, 392, 523, 659, 784],
      defeat: [247, 220, 196, 165, 147, 131, 123, 110],
    };
    const interval = theme === "boss" ? 330 : theme === "battle" ? 420 : 520;
    const tick = (): void => {
      const pattern = patterns[theme];
      const frequency = pattern[this.musicStep % pattern.length] ?? 220;
      this.musicStep += 1;
      const volume = 0.014 * this.settings.masterVolume * this.settings.musicVolume;
      const context = this.context();
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const now = context.currentTime;
      oscillator.type = theme === "boss" ? "sawtooth" : "triangle";
      oscillator.frequency.setValueAtTime(frequency, now);
      gain.gain.setValueAtTime(volume, now);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + (interval / 1000) * 0.82);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(now);
      oscillator.stop(now + (interval / 1000) * 0.85);
    };
    tick();
    this.musicTimer = window.setInterval(tick, interval);
    defenseAnalytics.track("music_started", { theme });
  }

  stopMusic(): void {
    if (this.musicTimer !== null) window.clearInterval(this.musicTimer);
    this.musicTimer = null;
    this.musicStep = 0;
    this.activeTheme = null;
  }

  bindUnlock(scene: Phaser.Scene): void {
    scene.input.once("pointerdown", () => void AudioManager.sharedContext?.resume());
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.stopMusic());
  }
}
